import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { Tabs, TabsContent } from '../../components/ui';
import { SplitViewTabBar } from '../../components/generic/SplitViewWithTabs';
import CapaDetailsTab from './components/tabs/CapaDetailsTab';
import CapaActionsTab from './components/tabs/CapaActionsTab';
import CapaTimelineTab from './components/tabs/CapaTimelineTab';
import CapaAuditLogTab from './components/tabs/CapaAuditLogTab';
import { useFlexibleList } from '../../hooks/useSWR';
import { DocumentTextIcon, ClipboardCheckIcon, ClockIcon, XIcon } from '../../components/ui/Icons';

/**
 * Custom Split View Panel for CAPA module with tabs
 * Tab 1: CAPA Details (same structure as SplitViewPanel)
 * Tab 2: CAPA Actions
 */
const CustomCapaSplitView = ({ 
  isOpen, 
  selectedRecord, 
  selectedRecordFiles, 
  config, 
  fields, 
  labels,
  onClose,
  onRecordUpdate // Callback to refresh CAPA data
}) => {
  const { theme } = useTheme();
  // Fetch CAPA actions count for the selected record
  const { data: capaActions = [] } = useFlexibleList(
    selectedRecord?.id ? `/capa-action/by-capa/${selectedRecord.id}` : null
  );
  
  // Fetch CAPA timeline entries count
  const { data: timelineEntries = [] } = useFlexibleList(
    selectedRecord?.id ? `/capa-timeline-entry/by-capa/${selectedRecord.id}` : null
  );
  
  // Fetch CAPA audit logs for the selected record
  const { data: auditLogs = [] } = useFlexibleList(
    selectedRecord?.id ? `/audit-log/master/3/${selectedRecord.id}` : null
  );
  
  // Fetch file attachments count (if the endpoint exists)
  const { data: fileAttachments = [] } = useFlexibleList(
    selectedRecord?.id ? `/file-attachment/entity/${config?.entityTypeId || 3}/${selectedRecord.id}` : null
  );
  
  // Define all tabs with their data
  const allTabs = [
    {
      value: "details",
      label: "Details",
      icon: <DocumentTextIcon className="w-4 h-4" />,
      badge: fileAttachments.length,
      badgeColor: "bg-green-600"
    },
    {
      value: "actions",
      label: "Actions",
      icon: <ClipboardCheckIcon className="w-4 h-4" />,
      badge: capaActions.length,
      badgeColor: "bg-blue-600"
    },
    {
      value: "timeline",
      label: "Timeline",
      icon: <ClockIcon className="w-4 h-4" />,
      badge: timelineEntries.length,
      badgeColor: "bg-orange-600"
    },
    {
      value: "audit-log",
      label: "Audit Log",
      icon: <ClipboardCheckIcon className="w-4 h-4" />,
      badge: auditLogs.length,
      badgeColor: "bg-red-600"
    }
  ];
  
  if (!isOpen) return null;

  return (
    <div 
      className={cn("hidden lg:block min-h-0 h-full pb-2")}
      style={{ width: config.ui?.drawerWidth || '420px' }}
    >
      <div className={cn("min-h-0 h-full rounded-xl border flex flex-col", theme.border.light, theme.colors.surface, theme.shadow.md)}>
        {/* Header */}
        <div className="px-4 py-3 border-b flex items-center justify-between">
          <div className={cn("text-sm font-semibold", theme.text.primary)}>
            {selectedRecord ? `CAPA - ${selectedRecord.code || selectedRecord.id}` : "Select a CAPA to view details"}
          </div>
          <button
            onClick={onClose}
            className={cn("p-1 rounded hover:opacity-80", theme.text.secondary)}
            title={labels.closeText || 'Close'}
          >
            <XIcon className="w-4 h-4" />
          </button>
        </div>

        {/* Tab Content */}
        {selectedRecord ? (
          <Tabs defaultValue="details" className="flex-1 flex flex-col min-h-0 px-3 py-2">
            <SplitViewTabBar tabs={allTabs} />
            
            <div className="flex-1 flex flex-col min-h-0">
              {/* Details Tab */}
              <TabsContent value="details" className="mt-0 flex-1 flex flex-col min-h-0">
                <CapaDetailsTab 
                  selectedRecord={selectedRecord}
                  selectedRecordFiles={selectedRecordFiles}
                  config={config}
                  fields={fields}
                />
              </TabsContent>

              {/* Actions Tab */}
              <TabsContent value="actions" className="mt-0 flex-1 flex flex-col min-h-0">
                <CapaActionsTab 
                  selectedRecord={selectedRecord}
                  onActionUpdate={onRecordUpdate}
                />
              </TabsContent>

              {/* Timeline Tab */}
              <TabsContent value="timeline" className="mt-0 flex-1 flex flex-col min-h-0">
                <CapaTimelineTab 
                  selectedRecord={selectedRecord}
                />
              </TabsContent>

              {/* Audit Log Tab */}
              <TabsContent value="audit-log" className="mt-0 flex-1 flex flex-col min-h-0">
                <CapaAuditLogTab 
                  selectedRecord={selectedRecord}
                />
              </TabsContent>
            </div>
          </Tabs>
        ) : (
          <div className={cn("p-4 text-sm", theme.text.secondary)}>
            {labels.noSelectionText || "No CAPA selected"}
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomCapaSplitView;
